import { filterUpload } from './filter-upload';
import { logError } from './logger';

export type FileWithRelativePath = {
  file: File;
  path: string;
  relativePath?: string[];
};

export const getFilesFromInput = async (
  fileList: FileList,
  keepFolderStructure = true
) => {
  const files: FileWithRelativePath[] = [];

  const fileArray = Array.from(fileList);

  for (const file of fileArray) {
    if (!filterUpload(file)) {
      continue;
    }

    if (keepFolderStructure) {
      const originalFile = file as File & { webkitRelativePath?: string };

      if (originalFile.webkitRelativePath) {
        const fileWithPath = applyFolderStructure(
          file,
          `/${originalFile.webkitRelativePath}`
        );

        files.push(fileWithPath);
      } else {
        files.push({ file, path: file.name });
      }
    } else {
      files.push({ file, path: file.name });
    }
  }

  return files;
};

export const getFileFromEntry = async (
  fileEntry: FileSystemFileEntry | FileSystemFileHandle
): Promise<File> => {
  try {
    return new Promise((resolve, reject) => {
      if (fileEntry instanceof FileSystemFileHandle) {
        return resolve(fileEntry.getFile());
      } else {
        return fileEntry.file(resolve, reject);
      }
    });
  } catch (error) {
    throw error;
  }
};

export const applyFolderStructure = (
  file: File,
  fullPath?: string
): FileWithRelativePath => {
  const fileWithPath: FileWithRelativePath = {
    file,
    path: fullPath || '',
  };

  if (fullPath && fullPath.indexOf('/') !== -1) {
    const pathParts = fullPath.split('/');
    const folderPath = pathParts.slice(1, -1);

    fileWithPath.relativePath = folderPath;
  }

  return fileWithPath;
};

const readDirectoryEntriesBatch = (reader: FileSystemDirectoryReader) =>
  new Promise<FileSystemEntry[]>((resolve, reject) => {
    reader.readEntries(resolve, reject);
  });

export const readAllDirectoryEntries = async (
  reader: FileSystemDirectoryReader,
  entries: FileSystemEntry[] = []
): Promise<FileSystemEntry[]> => {
  const batch = await readDirectoryEntriesBatch(reader);

  return batch.length
    ? readAllDirectoryEntries(reader, [...entries, ...batch])
    : entries;
};

export const getFilesFromDataTransferItems = async (
  dataTransferItems: DataTransferItem[],
  keepFolderStructure = true
) => {
  const supportsFileSystemAccessAPI =
    !keepFolderStructure &&
    'getAsFileSystemHandle' in DataTransferItem.prototype;
  const supportsWebkitGetAsEntry =
    'webkitGetAsEntry' in DataTransferItem.prototype;

  if (!supportsFileSystemAccessAPI && !supportsWebkitGetAsEntry) {
    logError('getAsFileSystemHandle and webkitGetAsEntry are not supported');
  }

  const files: FileWithRelativePath[] = [];

  async function handleEntry(entry: FileSystemEntry | FileSystemHandle) {
    const isFileEntry = 'isFile' in entry && entry.isFile;
    const isFileHandle = 'kind' in entry && entry.kind === 'file';
    const isDirectoryEntry = 'isDirectory' in entry && entry.isDirectory;
    const isDirectoryHandle = 'kind' in entry && entry.kind === 'directory';
    const fullPath = 'fullPath' in entry ? entry.fullPath : '';

    if (isFileEntry || isFileHandle) {
      try {
        const file: File = await getFileFromEntry(
          entry as FileSystemFileEntry | FileSystemFileHandle
        );

        if (filterUpload(file)) {
          const fileWithPath = keepFolderStructure
            ? applyFolderStructure(file, fullPath || undefined)
            : { file, path: fullPath || '' };

          files.push(fileWithPath);
        }
      } catch (error) {
        logError(error);
      }
    } else if (isDirectoryEntry || isDirectoryHandle) {
      if (entry instanceof FileSystemDirectoryHandle) {
        // @ts-ignore
        for await (const [_key, value] of entry.entries()) {
          await handleEntry(value);
        }
      } else {
        const directoryEntry = entry as FileSystemDirectoryEntry;

        for (const childEntry of await readAllDirectoryEntries(
          directoryEntry.createReader()
        )) {
          await handleEntry(childEntry);
        }
      }
    }
  }

  const droppedItems = [...dataTransferItems]
    .filter((item) => item.kind === 'file')
    .map((item) => ({
      entry: supportsFileSystemAccessAPI
        ? // @ts-ignore
          item.getAsFileSystemHandle()
        : supportsWebkitGetAsEntry
          ? item.webkitGetAsEntry()
          : null,
      file: item.getAsFile(),
    }));

  for (const { entry, file } of droppedItems) {
    const resolvedEntry = await entry;

    if (resolvedEntry) {
      await handleEntry(resolvedEntry);
    } else if (file && filterUpload(file)) {
      files.push({ file, path: file.name });
    }
  }

  return files;
};
